<template>
  <Dialog :open="props.show" @update:open="onClose">
    <DialogContent class="sm:max-w-[500px]">
      <DialogHeader>
        <DialogTitle>Cancel Order</DialogTitle>
      </DialogHeader>
      <SectionDivider className="my-4" />
      <div>
        <p>The customer will be notified if this order is cancelled. Are you sure you want to cancel?</p>
        <SectionDivider className="my-4" />
        <div class="flex flex-col gap-4">
          <h3 class="text-grey-light-900 font-semibold">Cancellation Amendments</h3>
          <div class="flex gap-2 items-center">
            <SwitchComponent
              :disabled="isCancelRequestInFlight"
              :model-value="isChangingCancellationTerms"
              @update:model-value="isChangingCancellationTerms = !isChangingCancellationTerms"
            />
            <LabelComponent>Change Cancellation Terms</LabelComponent>
          </div>
          <div v-if="isChangingCancellationTerms" class="grid grid-cols-1 gap-4">
            <div class="col-span-1 flex flex-col gap-2">
              <LabelComponent>Propose new cancellation terms</LabelComponent>
              <SelectInput
                :disabled="isCancelRequestInFlight"
                :items="['Deposit', 'Flat Fee', 'Percentage', 'No Fee']"
                v-model="cancellationType"
              />
            </div>
            <div v-if="cancellationType === 'Percentage'" class="col-span-1">
              <NumberInput :disabled="isCancelRequestInFlight" v-model="percentage" :min="0" :max="100" />
            </div>
            <div
              v-if="
                cancellationType === 'Flat Fee' || cancellationType === 'Deposit' || cancellationType === 'Percentage'
              "
              class="col-span-1"
            >
              <CurrencyInput
                :label="`New Cancellation Fee`"
                :disabled="
                  isCancelRequestInFlight || cancellationType === 'Deposit' || cancellationType === 'Percentage'
                "
                v-model="amount"
              />
            </div>
            <div class="flex flex-col gap-2">
              <TextBoxInput
                aria-required="true"
                :disabled="isCancelRequestInFlight"
                :label="`Message*`"
                v-model="message"
              />
              <span v-if="messageError !== ''" class="text-error-700 font-medium text-xs">
                {{ messageError }}
              </span>
            </div>
          </div>
        </div>
        <SectionDivider className="my-4" />
        <p v-if="cancelRequestError" class="text-red-700">{{ cancelRequestError }}</p>
        <LayupButton
          @click="onCancelOrder"
          :disabled="isCancelRequestInFlight"
          :label="'Cancel Order'"
          type="submit"
          :isLoading="isCancelRequestInFlight"
        />
      </div>
    </DialogContent>
  </Dialog>
</template>
<script setup lang="ts">
import CurrencyInput from '@/components/common/CurrencyInput.vue';
import LayupButton from '@/components/common/LayupButton.vue';
import NumberInput from '@/components/common/NumberInput.vue';
import SectionDivider from '@/components/common/SectionDivider.vue';
import SelectInput from '@/components/common/SelectInput_v2.vue';
import TextBoxInput from '@/components/common/TextBoxInput.vue';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import LabelComponent from '@/components/ui/label/LabelComponent.vue';
import SwitchComponent from '@/components/ui/switch/SwitchComponent.vue';
import { NetworkError } from '@/errors';
import type { Order } from '@/models/settlements';
import { getOrderByIdWithPlansPaymentsAndConnectors } from '@/services/orders';
import { useMerchantStore } from '@/stores/merchants';
import { useOrderStore } from '@/stores/orders';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { updateSuspendedOrder } from '@/utils/order.utils';
import { planAmount } from '@/utils/paymentPlan.utils';
import { AxiosError } from 'axios';
import { ref, watch, type WatchHandle } from 'vue';
import type { ModelCancellationPolicy, ModelOrderResponse } from '@layup/api-sdk';

type CancellationType = 'Deposit' | 'Flat Fee' | 'Percentage' | 'No Fee';

const props = defineProps<{
  show: boolean;
  order?: ModelOrderResponse;
}>();

const emit = defineEmits(['update:show']);
const onClose = (): void => emit('update:show');

const merchantStore = useMerchantStore();
const orderStore = useOrderStore();

const isChangingCancellationTerms = ref(false);
const cancellationType = ref<'Deposit' | 'Flat Fee' | 'Percentage' | 'No Fee'>();
const percentage = ref<number>(0);
const amount = ref<number>(0);
const message = ref<string>('');
const messageError = ref<string>('');

const isCancelRequestInFlight = ref(false);
const cancelRequestError = ref('');

/*
 * We have to watch props.show. When the value changes, we need to
 * manually reset the state and register/destroy watchers because the
 * modal is not unmounted when it closes.
 */
const watchHandles: WatchHandle[] = [];
watch(
  () => props.show,
  (isShowing) => {
    if (isShowing) {
      const order = props.order;
      const orderAmount = order?.plans?.reduce((total, plan) => total + planAmount(plan), 0) || 0;

      isChangingCancellationTerms.value = false;
      cancellationType.value = 'Deposit';
      message.value = '';
      messageError.value = '';
      cancelRequestError.value = '';

      // Re-calculate amount and percentage.
      const handleCancellationTypeChange = (cancellationType: CancellationType | undefined): void => {
        percentage.value = 0;

        if (cancellationType === 'Deposit' && props.order?.depositAmount) {
          amount.value = props.order.depositAmount;
        } else {
          amount.value = 0;
        }
      };

      watchHandles.push(watch(cancellationType, handleCancellationTypeChange, { immediate: true }));

      // Re-calculate amount
      const handlePercentageChange = (percentage: number): void => {
        if (cancellationType.value === 'Percentage') amount.value = Math.round(orderAmount * (percentage / 100));
      };

      watchHandles.push(watch(percentage, handlePercentageChange));

      // Clear message text input error
      const handleMessageChange = (): void => {
        messageError.value = '';
      };

      watchHandles.push(watch(message, handleMessageChange));
    } else {
      // Destroy all watchers
      while (watchHandles.length) {
        const unwatch = watchHandles.pop()!;
        unwatch();
      }
    }
  },
);

const validateForm = (): boolean => {
  let isValid = true;

  if (!isChangingCancellationTerms.value) return isValid;

  if (message.value.trim() === '') {
    messageError.value = 'Please enter message (required)';
    isValid = false;
  }

  return isValid;
};

const onCancelOrder = async () => {
  cancelRequestError.value = '';

  if (!validateForm()) return;

  isCancelRequestInFlight.value = true;

  try {
    let cancellationTerms: ModelCancellationPolicy;

    /* Use new cancellation terms */
    if (isChangingCancellationTerms.value) {
      if (cancellationType.value === 'Flat Fee') {
        cancellationTerms = {
          name: 'FlatFee',
          definition: {
            flatFee: Math.round(amount.value),
          },
        };
      } else if (cancellationType.value === 'Percentage') {
        cancellationTerms = {
          name: 'Percentage',
          definition: {
            percentage: percentage.value,
          },
        };
      } else if (cancellationType.value === 'Deposit') {
        cancellationTerms = {
          name: 'Deposit',
          definition: {
            deposit: true,
          },
        };
      } else {
        cancellationTerms = {
          name: 'FlatFee',
          definition: {
            flatFee: 0,
          },
        };
      }
    } else {
      /* Use existing cancellation terms */
      cancellationTerms = {
        name: merchantStore.merchant?.cancellationTerms?.definition?.[0].policy?.name || 'Combination',
        definition: {
          deposit: merchantStore.merchant?.cancellationTerms?.definition?.[0].policy?.definition?.deposit,
          percentage: merchantStore.merchant?.cancellationTerms?.definition?.[0].policy?.definition?.percentage,
          flatFee: merchantStore.merchant?.cancellationTerms?.definition?.[0].policy?.definition?.flatFee,
          percentageShouldUseTotalPaid:
            merchantStore.merchant?.cancellationTerms?.definition?.[0].policy?.definition?.percentageShouldUseTotalPaid,
          includeIncurredOrderFees:
            merchantStore.merchant?.cancellationTerms?.definition?.[0].policy?.definition?.includeIncurredOrderFees,
        },
      };
    }

    const orderToUpdate: Order = {
      _id: props.order?._id || '',
      reference: props.order?.reference || '',
      state: 'CANCELLING',
      initiator: { _id: merchantStore.merchant?._id || '', name: merchantStore.merchant?.name || '' },
    };

    await updateSuspendedOrder(orderToUpdate?._id!, orderToUpdate, cancellationTerms);

    const updatedOrder = await getOrderByIdWithPlansPaymentsAndConnectors(orderStore.order?._id!);
    orderStore.updateOrder(updatedOrder);
    emit('update:show');
    addMetric('cancellation-modal', { status: 'success  ', channel: 'merchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('cancellation-modal', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('cancellation-modal', { status: 'error', channel: 'merchant' });
    }

    if (error instanceof AxiosError) {
      cancelRequestError.value = `Failed to cancel order: ${error.response?.data}`;
    } else {
      cancelRequestError.value = `Failed to cancel order: ${error}`;
    }
  } finally {
    isCancelRequestInFlight.value = false;
  }
};
</script>
